<template>
  <VFormFeedback :error="errors.name && errors.name[0]" :class="formFeedbackVariationClassName">
    <VInput v-model="payload.name" :config="{ label: 'Area name', required: true }" class="slim" />
  </VFormFeedback>
  <VFormFeedback :error="errors.areas && errors.areas[0]" :class="formFeedbackVariationClassName">
    <PatchAreaSelector
      v-model="payload.areas"
      :search-performer="locationSearchDataClient"
      :is-bulk="areAreasBulk"
      placeholder="Select area"
      variant-class-name="slim"
    />
    <VSwitch v-model="areAreasBulk" title="Bulk" class="primary" />
  </VFormFeedback>
  <VFormFeedback :error="errors.type && errors.type[0]" :class="formFeedbackVariationClassName">
    <ManagerPatchFormTypes :class="typesVariationClassName">
      <li>
        <VRadio
          v-model="payload.type"
          value="sale"
          :name="saleTypeName"
          label="Sales"
          class="manager-patch"
        />
      </li>
      <li>
        <VRadio
          v-model="payload.type"
          value="let"
          :name="saleTypeName"
          label="Lettings"
          class="manager-patch"
        />
      </li>
      <li>
        <VRadio
          v-model="payload.type"
          value="both"
          :name="saleTypeName"
          label="Both"
          class="manager-patch"
        />
      </li>
    </ManagerPatchFormTypes>
  </VFormFeedback>
  <div>
    <VBtn class="full-width slim" type="button" :loading="isLoading" @click="submit">{{
      submitText
    }}</VBtn>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type { PropType } from 'vue';
  import type { TSearchPerformer } from '~/misc/location/search/types';
  import type { TNewPatch, TPatch } from '~/misc/patch/types';
  import type { TFormPatch } from '~/misc/patch/form/types';

  // Services
  import { ManagerLocationSearchDataClient } from '~/modules/manager/location/search/services/location-search-data-client';

  // Models
  import { ManagerCompanyModel } from '~/modules/manager/models/company.model';
  import { ManagerPatchesModel } from '~/modules/manager/models/patches.model';

  // Composables
  import { usePatchForm } from '~/misc/patch/form/composables/use-patch-form';

  // Utils
  import { getUniqId } from '~/misc/utils/uniq-id';

  // Components
  import VFormFeedback from '~/misc/v-form-feedback';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';
  import VRadio from '~/misc/v-radio';
  import VSwitch from '~/misc/v-switch';
  import ManagerPatchFormTypes from '~/modules/manager/patch/form/types/ManagerPatchFormTypes.vue';
  import PatchAreaSelector from '~/misc/patch-area-selector';

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    patch: {
      type: Object as PropType<TFormPatch | null>,
      default: null,
    },
    submitText: {
      type: String,
      default: 'Add',
    },
    formFeedbackVariationClassName: {
      type: String,
      default: '',
    },
    typesVariationClassName: {
      type: String,
      default: '',
    },
  });

  const saleTypeName: string = `sale_type_${getUniqId()}`;

  const areAreasBulk: Ref<boolean> = ref<boolean>(false);

  const computedPatch: ComputedRef<TFormPatch | null> = computed<TFormPatch | null>(
    () => props.patch
  );

  const emit = defineEmits<{
    (e: 'upsert', patch: TPatch): void;
  }>();

  const locationSearchDataClient: TSearchPerformer = new ManagerLocationSearchDataClient();
  const companyModel: ManagerCompanyModel = new ManagerCompanyModel();
  const patchesModel: ManagerPatchesModel = new ManagerPatchesModel();

  const update = (patchId: number, patch: TNewPatch) => patchesModel.update(patchId, patch);

  const store = async (payload: TNewPatch): Promise<TPatch | null> =>
    props.companyId ? await companyModel.createPatch(props.companyId, payload) : null;

  const emitUpsert = (patch: TPatch) => emit('upsert', patch);

  const { payload, errors, isLoading, submit } = usePatchForm(
    computedPatch,
    update,
    store,
    emitUpsert
  );
</script>
